import React, { useMemo } from 'react';
import { ButtonBase, IconButton } from '@material-ui/core';
import { ReactComponent as LeftArrow } from 'common-assets/assets/icons/arrow-left-filled.svg';
import { ReactComponent as RightArrow } from 'common-assets/assets/icons/arrow-right-filled.svg';
import plLocale from 'date-fns/locale/pl';
import enLocale from 'date-fns/locale/en-US';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ToolbarComponentProps } from '@material-ui/pickers/Picker/Picker';
import { DatePickerSize } from './tools/DatePicker.const';
import clsx from 'clsx';
import { Typography } from '../Typography/Typography';
import { useFocusTrap } from 'common-react';
import { useAutoFocus } from '../utils/useAutoFocus';
import { getKeyboardKey } from '../wcag/getKeyboardKey';

import useStyles from './DatePicker.style';


export const buildMonths = (locale: Locale) =>
  Array.from({ length: 12 }, (_, i) =>
    format(new Date(2020, i, 1), "LLLL", { locale }),
  );

const localeMap = {
  en: enLocale,
  pl: plLocale,
};

const keyMap = {
  isArrowLeftKey: (d: Date) => d.setMonth(d.getMonth() - 1),
  isArrowRightKey: (d: Date) => d.setMonth(d.getMonth() + 1),
  isArrowUpKey: (d: Date) => d.setFullYear(d.getFullYear() + 1),
  isArrowDownKey: (d: Date) => d.setFullYear(d.getFullYear() - 1),
} as const;

export const DatePickerToolbar = ({
  date,
  onChange,
}: ToolbarComponentProps) => {
  const [t, i18n] = useTranslation('components');
  const locale = localeMap[i18n.language as keyof typeof localeMap] || enLocale;
  const months = useMemo(() => buildMonths(locale), [locale]);
  const c = useStyles({ sizes: DatePickerSize.DEFAULT });
  const safeDate = date ? new Date(date) : new Date();
  const ref = useFocusTrap();
  const autoFocusRef = useAutoFocus<HTMLButtonElement>();

  const changeYear = (diff: number) => {
    const next = new Date(safeDate.toString());
    next.setFullYear(next.getFullYear() + diff);
    onChange(next, false);
  };

  const changeMonth = (monthIndex: number) => {
    const next = new Date(safeDate.toString());
    next.setMonth(monthIndex);
    onChange(next, true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!date) return;

    const keys = getKeyboardKey(e.nativeEvent);

    const next = new Date(date);

    const matched = Object.entries(keyMap).find(([key]) => keys[key as keyof typeof keys]);

    if (!matched) return;

    matched[1](next);

    e.preventDefault();
    onChange(next, false);
  };

  return (
    <div className={c.toolbarWrapper} ref={ref} onKeyDown={handleKeyDown} role="toolbar" tabIndex={-1}>
      <div className={c.toolbarYearRow}>
        <IconButton onClick={() => changeYear(-1)} aria-label={t("datepicker.previous-year")} tabIndex={0}>
          <LeftArrow />
        </IconButton>
        <Typography variant="h4" fontWeight="bold">
          {date?.getFullYear()}
        </Typography>
        <IconButton onClick={() => changeYear(1)} aria-label={t("datepicker.next-year")} tabIndex={0}>
          <RightArrow />
        </IconButton>
      </div>
      <div className={c.monthGrid}>
        {months.map((month, idx) => (
          <ButtonBase
            key={month}
            onClick={() => changeMonth(idx)}
            tabIndex={idx === safeDate.getMonth() ? 0 : -1}
            ref={idx === safeDate.getMonth() ? autoFocusRef : undefined}
            type="button"
            className={clsx({
              [c.monthButton]: true,
              [c.monthButtonSelected]: safeDate.getMonth() === idx,
            })}
          >
            {month}
          </ButtonBase>
        ))}
      </div>
    </div>
  );
};